import React, { useState, useEffect, useMemo, useRef } from 'react';
import { avisar } from '../components/confirmacao';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import apiService from '../services/apiService';
import Loading from '../components/Loading';
import { dataHoraBR } from '../components/TelegramFeedbackFields';
import './UserRegister.css';

// Estado de acesso vindo da API (/liststoresbyuser e /listusers). O mapeamento desta tela
// monta cada usuário campo a campo, e as telas de edição só recebem o que for copiado aqui.
const camposDeAcesso = (u) => ({
    is_locked: !!u.is_locked,
    locked_until: u.locked_until || null,
    failed_login_count: u.failed_login_count || 0,
    last_lockout_at: u.last_lockout_at || null,
    last_lockout_ip: u.last_lockout_ip || null,
    last_login_at: u.last_login_at || null,
    last_login_ip: u.last_login_ip || null,
});

// Login bloqueado por excesso de senhas erradas: o desbloqueio fica na tela de edição.
const CadeadoLogin = ({ user }) => (user.is_locked ? (
    <span
        className="user-locked-badge"
        title={`Login bloqueado até ${dataHoraBR(user.locked_until)} por excesso de senhas erradas. Desbloqueie na tela de edição.`}
    >
        🔒 bloqueado
    </span>
) : null);

const EXPAND_CACHE_KEY = 'userRegister_expandState';

const UserRegister = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const scrollYParam = useRef(searchParams.get('scrollY')).current;
    const [searchTerm, setSearchTerm] = useState('');
    const [companies, setCompanies] = useState([]);
    const [unlinkedUsers, setUnlinkedUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Restaurar estados de expansão do cache quando voltando de outra página
    const cachedExpand = useRef(() => {
        if (!scrollYParam) return null;
        try {
            const raw = sessionStorage.getItem(EXPAND_CACHE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    }).current;
    const expandCache = useRef(typeof cachedExpand === 'function' ? cachedExpand() : cachedExpand).current;

    const [expandedCompanies, setExpandedCompanies] = useState(expandCache?.expandedCompanies || {});
    const [expandedStores, setExpandedStores] = useState(expandCache?.expandedStores || {});
    const [expandedUnits, setExpandedUnits] = useState(expandCache?.expandedUnits || {});
    const [selectedCompanyId, setSelectedCompanyId] = useState(expandCache?.selectedCompanyId || null);
    const [expandedAdminSections, setExpandedAdminSections] = useState(expandCache?.expandedAdminSections || {});
    const [fetchedAdmins, setFetchedAdmins] = useState({});
    const [removeModal, setRemoveModal] = useState({ open: false, user: null, storeId: null, company: null, scope: 'user', loading: false, error: '' });
    // Migração de loja: troca o store_id na MESMA linha de salesman (o salesman_id não muda),
    // então turma/fase, pushtokens, foto e onboarding seguem com a pessoa e o histórico fica na
    // loja onde aconteceu. Ver docs/fases-atendimento-plano.md §2.9 na api.
    const [migrateModal, setMigrateModal] = useState({
        open: false, user: null, fromStore: null, company: null, targetStoreId: '', loading: false, error: '', done: null,
    });

    const pendingScrollY = useRef(null);

    // Limpar scrollY da URL e guardar o valor para restaurar depois do loading
    useEffect(() => {
        if (scrollYParam) {
            const url = new URL(window.location.href);
            url.searchParams.delete('scrollY');
            window.history.replaceState(null, '', url.pathname + (url.search || ''));
            pendingScrollY.current = parseInt(scrollYParam, 10);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Restaurar scroll quando loading terminar e houver posição pendente
    useEffect(() => {
        if (!loading && pendingScrollY.current !== null) {
            const targetY = pendingScrollY.current;
            pendingScrollY.current = null;
            const scrollContainer = document.querySelector('.app-main');
            if (scrollContainer) {
                let attempts = 0;
                const tryScroll = () => {
                    scrollContainer.scrollTop = targetY;
                    attempts++;
                    if (Math.abs(scrollContainer.scrollTop - targetY) > 10 && attempts < 30) {
                        requestAnimationFrame(tryScroll);
                    }
                };
                requestAnimationFrame(tryScroll);
            }
        }
    }, [loading]);

    // Salvar estado de expansão e scroll antes de navegar
    const saveStateAndNavigate = (path, state = {}) => {
        const scrollContainer = document.querySelector('.app-main');
        const scrollY = scrollContainer ? Math.round(scrollContainer.scrollTop) : 0;
        // Salvar estado de expansão no sessionStorage
        try {
            sessionStorage.setItem(EXPAND_CACHE_KEY, JSON.stringify({
                expandedCompanies, expandedStores, expandedUnits,
                expandedAdminSections, selectedCompanyId
            }));
        } catch { /* ignore */ }
        navigate(path, { state: { ...state, scrollY } });
    };

    // --- Carregar dados da API (recarrega ao navegar de volta) ---
    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    const loadData = async () => {
        try {
            setLoading(true);
            setError('');
            const [response, usersResponse] = await Promise.all([
                apiService.listStoresByUser(),
                apiService.listUsers().catch(() => null)
            ]);

            // Verificar se houve erro na chamada
            if (response.success === false) {
                setError(response.message || 'Erro ao carregar dados.');
                return;
            }

            const data = response.data || response;

            if (!data.companies) {
                setError('Resposta inesperada da API. Verifique o console.');
                console.error('Estrutura inesperada:', Object.keys(data));
                return;
            }

            // Mapear dados da API para o formato do componente
            const mappedCompanies = data.companies.map((company) => {
                // Suportar tanto a estrutura com units quanto a estrutura legada com stores
                const units = company.units || [];
                const legacyStores = company.stores || [];

                // Se houver units, usar a nova estrutura; caso contrário, agrupar stores por unit
                let processedUnits = units;
                if (units.length === 0 && legacyStores.length > 0) {
                    // Agrupar stores por unit_id para compatibilidade com dados antigos
                    const unitsMap = new Map();
                    legacyStores.forEach(store => {
                        const unitId = store.unit_id;
                        const unitName = store.unit_name;
                        const unitNickname = store.unit_nickname;

                        if (!unitsMap.has(unitId)) {
                            unitsMap.set(unitId, {
                                unit_id: unitId,
                                unit_name: unitName,
                                unit_nickname: unitNickname,
                                stores: []
                            });
                        }
                        unitsMap.get(unitId).stores.push(store);
                    });
                    processedUnits = Array.from(unitsMap.values()).sort((a, b) => a.unit_id - b.unit_id);
                }

                return {
                    id: company.company_id,
                    company_id: company.company_id,
                    name: company.company_name,
                    subdomain: company.subdomain,
                    bucket_name: company.bucket_name,
                    default_ia_model: company.default_ia_model,
                    unlock_date: company.unlock_date,
                    units: processedUnits.map((unit) => ({
                        id: unit.unit_id,
                        name: unit.unit_name,
                        nickname: unit.unit_nickname,
                        stores: (unit.stores || []).map((store) => ({
                            id: store.store_id,
                            name: store.store_display_name || store.store_name,
                            store_id: store.store_id,
                            store_name: store.store_name,
                            store_display_name: store.store_display_name,
                            app_version_name: store.app_version_name,
                            unlock_date: store.unlock_date || null,
                            users: (store.salesman || []).map((s) => ({
                                id: s.id,
                                user_id: s.user_id,
                                fullname: s.fullname,
                                username: s.username || s.name || '',
                                role: s.role || 'vendedor',
                                created_at: s.created || '',
                                recordings_start: s.recordings_start || '',
                                is_visible: s.is_visible !== undefined ? s.is_visible : true,
                                is_active: s.is_active !== undefined ? s.is_active : true,
                                feedback_read_enabled: s.feedback_read_enabled || false,
                                feedback_read_enabled_start_date: s.feedback_read_enabled_start_date || null,
                                app_version_name: s.app_version_name || null,
                                onboarding_completed_at: s.onboarding_completed_at || null,
                                ...camposDeAcesso(s),
                                hidden: false,
                            })),
                        })),
                    })),
                };
            });

            setCompanies(mappedCompanies);

            // Processar admin users do listUsers
            const adminsByCompany = {};
            if (usersResponse) {
                const allUsers = usersResponse.users || usersResponse.data?.users || (Array.isArray(usersResponse) ? usersResponse : []);
                const usersList = Array.isArray(allUsers) ? allUsers : [];
                const adminRoles = ['admin', 'manager', 'director', 'supervisor'];
                const adminRows = usersList.filter(u => adminRoles.includes(u.role));

                // Criar mapa de store_id -> store_name usando mappedCompanies
                const storeNameMap = {};
                mappedCompanies.forEach(c => {
                    c.units.forEach(u => {
                        u.stores.forEach(s => {
                            storeNameMap[s.id] = s.name;
                        });
                    });
                });

                // A API retorna linhas planas (uma por user+store), agrupar por user_id+company_id
                const groupedMap = {};
                adminRows.forEach(u => {
                    const companyId = u.company_id;
                    if (!companyId) return;
                    const key = `${u.user_id}-${companyId}`;
                    if (!groupedMap[key]) {
                        groupedMap[key] = {
                            id: u.id || u.salesman_id,
                            user_id: u.user_id,
                            fullname: u.fullname,
                            username: u.username || u.name || '',
                            role: u.role,
                            created_at: u.created || u.created_at || '',
                            company_id: companyId,
                            // Telegram: o agrupamento monta a linha campo por campo, então sem
                            // isto a tela de edição da CHEFIA abriria com os campos vazios e
                            // salvar apagaria o vínculo — silenciosamente.
                            telegram_username: u.telegram_username,
                            telegram_linked: u.telegram_linked,
                            telegram_linked_at: u.telegram_linked_at,
                            feedback_telegram_enabled: u.feedback_telegram_enabled,
                            feedback_telegram_mode: u.feedback_telegram_mode,
                            // Mesmo motivo, e aqui dói mais: sem repassar o telefone, a tela
                            // de edição abre vazia e salvar APAGA o número de quem já tem.
                            whatsapp_phone: u.whatsapp_phone,
                            whatsapp_enabled: u.whatsapp_enabled,
                            whatsapp_opt_in_at: u.whatsapp_opt_in_at,
                            // Bloqueio de login e último login: mesmo motivo — a tela de edição
                            // só enxerga o que for copiado aqui.
                            ...camposDeAcesso(u),
                            storeIds: [],
                            storeNames: [],
                        };
                    }
                    // Coletar store de cada linha plana
                    if (u.store_id) {
                        const group = groupedMap[key];
                        if (!group.storeIds.includes(u.store_id)) {
                            group.storeIds.push(u.store_id);
                            const displayName = u.store_display_name || u.store_name || storeNameMap[u.store_id] || '';
                            if (displayName) group.storeNames.push(displayName);
                        }
                    }
                });

                Object.values(groupedMap).forEach(admin => {
                    const companyId = admin.company_id;
                    if (!adminsByCompany[companyId]) {
                        adminsByCompany[companyId] = [];
                    }
                    adminsByCompany[companyId].push(admin);
                });
            }
            setFetchedAdmins(adminsByCompany);

            // Expandir todas as empresas por padrão (se não há estado restaurado do cache)
            if (!expandCache) {
                const expanded = mappedCompanies.reduce(
                    (acc, c) => ({ ...acc, [c.id]: true }), {}
                );
                setExpandedCompanies(expanded);
            }

            // Usuários sem empresa
            const unassigned = data.unassigned_users || [];
            if (unassigned.length > 0) {
                setUnlinkedUsers(
                    unassigned.map((u) => ({
                        id: u.id,
                        fullname: u.fullname,
                        username: u.username,
                        role: u.role || 'sem perfil',
                    }))
                );
            }
        } catch (err) {
            console.error('Erro ao carregar dados:', err);
            setError('Erro ao carregar dados. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    // --- Busca ---
    const normalizeStr = (s) =>
        (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    const filteredData = useMemo(() => {
        let result = { companies, unlinkedUsers };

        // Filtro por empresa selecionada
        if (selectedCompanyId) {
            result = {
                ...result,
                companies: result.companies.filter((c) => c.id === selectedCompanyId),
            };
        }

        // Filtro por busca textual
        if (searchTerm.trim()) {
            const term = normalizeStr(searchTerm);

            const filteredCompanies = result.companies
                .map((company) => {
                    const companyMatch = normalizeStr(company.name).includes(term);
                    const filteredUnits = company.units
                        .map((unit) => {
                            const unitMatch = normalizeStr(unit.name).includes(term) || normalizeStr(unit.nickname).includes(term);
                            const filteredStores = unit.stores
                                .map((store) => {
                                    const storeMatch = normalizeStr(store.name).includes(term);
                                    const filteredUsers = store.users.filter(
                                        (u) =>
                                            normalizeStr(u.fullname).includes(term) ||
                                            normalizeStr(u.username).includes(term) ||
                                            normalizeStr(u.role).includes(term)
                                    );
                                    if (companyMatch || unitMatch || storeMatch || filteredUsers.length > 0) {
                                        return {
                                            ...store,
                                            users: companyMatch || unitMatch || storeMatch ? store.users : filteredUsers,
                                        };
                                    }
                                    return null;
                                })
                                .filter(Boolean);

                            if (filteredStores.length > 0) {
                                return { ...unit, stores: filteredStores };
                            }
                            return null;
                        })
                        .filter(Boolean);

                    if (filteredUnits.length > 0) {
                        return { ...company, units: filteredUnits };
                    }
                    return null;
                })
                .filter(Boolean);

            const filteredUnlinked = result.unlinkedUsers.filter(
                (u) =>
                    normalizeStr(u.fullname).includes(term) ||
                    normalizeStr(u.username).includes(term) ||
                    normalizeStr(u.role).includes(term)
            );

            result = { companies: filteredCompanies, unlinkedUsers: filteredUnlinked };
        }

        return result;
    }, [searchTerm, selectedCompanyId, companies, unlinkedUsers]);

    // --- Expand/Collapse ---
    const toggleCompany = (companyId) => {
        setExpandedCompanies((prev) => ({ ...prev, [companyId]: !prev[companyId] }));
    };

    const toggleStore = (storeId) => {
        setExpandedStores((prev) => ({ ...prev, [storeId]: !prev[storeId] }));
    };

    const toggleUnit = (unitId) => {
        setExpandedUnits((prev) => ({ ...prev, [unitId]: !prev[unitId] }));
    };

    const toggleAdminSection = (companyId) => {
        setExpandedAdminSections((prev) => ({ ...prev, [companyId]: !prev[companyId] }));
    };

    // Quem NÃO é vendedor aparece só na seção de Administradores; quem é, só na lista da loja.
    // Antes o corte era por uma lista fixa de roles ('admin','manager','director','supervisor'),
    // então diretor/gerente/coordenador entravam nas DUAS listas: na da loja porque têm linha em
    // salesman, e na de administradores porque a role batia. Role nova (coordenador, por exemplo)
    // também ficava de fora da seção de admins sem ninguém notar.
    const isSalesman = (user) => user.role === 'salesman' || user.role === 'vendedor';

    const getCompanyAdmins = (company) => {
        const adminMap = {};

        // Não-vendedores que estão vinculados a alguma loja
        company.units.forEach((unit) => {
            unit.stores.forEach((store) => {
                store.users.forEach((user) => {
                    if (!isSalesman(user)) {
                        const key = user.username || user.id;
                        if (!adminMap[key]) {
                            adminMap[key] = { ...user, storeNames: [], storeIds: [] };
                        }
                        adminMap[key].storeNames.push(store.name);
                        adminMap[key].storeIds.push(store.id);
                    }
                });
            });
        });

        // Admins buscados via listUsers (que podem não estar nas lojas)
        const fetched = fetchedAdmins[company.id] || [];
        fetched.forEach((admin) => {
            const key = admin.username || admin.id;
            if (!adminMap[key]) {
                adminMap[key] = { ...admin };
            }
        });

        return Object.values(adminMap);
    };

    // --- Ações de Usuário ---
    const handleEdit = (user, company) => {
        saveStateAndNavigate('/users/edit', { user, company });
    };

    // scope 'company' (seção de administradores): tira o acesso da pessoa a ESTA empresa e
    // mantém o login. Chefia não tem linha em salesman, então o DELETE /user por salesman_id
    // recusava com "salesman_id e company_id são obrigatórios" — e, se passasse, apagaria o
    // login inteiro, levando junto o acesso às outras empresas (Aliado Admin inclusive).
    // scope 'user' (lista da loja): vendedor, remove o usuário como antes.
    const handleRemove = (user, storeId, company, scope = 'user') => {
        setRemoveModal({ open: true, user, storeId, company, scope, loading: false, error: '' });
    };

    const removeCompanyAccess = async (user, company) => {
        if (!user.user_id) {
            throw new Error('Usuário sem user_id — recarregue a página e tente de novo');
        }
        // store_ids vazio: o PUT /user apaga as permissões da empresa e não reinsere nenhuma
        const result = await apiService.updateUser({
            user_id: user.user_id,
            company_id: company.id,
            fullname: user.fullname,
            username: user.username,
            role: user.role,
            store_ids: [],
        });
        if (!result.success) {
            throw new Error(result.message || 'Erro ao remover acesso à empresa');
        }
        setFetchedAdmins((prev) => ({
            ...prev,
            [company.id]: (prev[company.id] || []).filter((a) => a.user_id !== user.user_id),
        }));
        setCompanies((prev) =>
            prev.map((c) => (c.id !== company.id ? c : {
                ...c,
                units: c.units.map((unit) => ({
                    ...unit,
                    stores: unit.stores.map((store) => ({
                        ...store,
                        users: store.users.filter((u) => u.user_id !== user.user_id),
                    })),
                })),
            }))
        );
    };

    const confirmRemove = async () => {
        const { user, storeId, company, scope } = removeModal;
        setRemoveModal((prev) => ({ ...prev, loading: true, error: '' }));
        if (scope === 'company') {
            try {
                await removeCompanyAccess(user, company);
                cancelRemove();
            } catch (error) {
                setRemoveModal((prev) => ({ ...prev, loading: false, error: error.message || 'Erro ao remover acesso' }));
            }
            return;
        }
        try {
            await apiService.deleteUser({ salesman_id: user.id, company_id: company.id });
            setCompanies((prev) =>
                prev.map((c) => ({
                    ...c,
                    units: c.units.map((unit) => ({
                        ...unit,
                        stores: unit.stores.map((store) => {
                            if (storeId && store.id !== storeId) return store;
                            return {
                                ...store,
                                users: store.users.filter((u) => u.id !== user.id),
                            };
                        }),
                    })),
                }))
            );
            cancelRemove();
        } catch (error) {
            setRemoveModal((prev) => ({ ...prev, loading: false, error: error.message || 'Erro ao remover usuário' }));
        }
    };

    const cancelRemove = () => {
        setRemoveModal({ open: false, user: null, storeId: null, company: null, scope: 'user', loading: false, error: '' });
    };

    // --- Migração de loja ---
    const handleMigrate = (user, store, company) => {
        setMigrateModal({
            open: true, user, fromStore: store, company, targetStoreId: '', loading: false, error: '', done: null,
        });
    };

    const cancelMigrate = () => {
        setMigrateModal({
            open: false, user: null, fromStore: null, company: null, targetStoreId: '', loading: false, error: '', done: null,
        });
    };

    // Lojas da MESMA empresa, menos a atual: migrar entre empresas é schema diferente, o que
    // criaria outro salesman_id e partiria o histórico. A API também recusa.
    const migrateOptions = (company, fromStoreId) =>
        (company?.units || [])
            .flatMap((unit) => (unit.stores || []).map((s) => ({ ...s, unitName: unit.name })))
            .filter((s) => s.id !== fromStoreId);

    const confirmMigrate = async () => {
        const { user, fromStore, company, targetStoreId } = migrateModal;
        if (!targetStoreId) return;
        setMigrateModal((prev) => ({ ...prev, loading: true, error: '' }));
        try {
            const result = await apiService.changeUserStore({
                company_id: company.id,
                salesman_id: user.id,
                store_id: Number(targetStoreId),
            });
            if (!result.success) {
                setMigrateModal((prev) => ({ ...prev, loading: false, error: result.message }));
                return;
            }
            const destino = Number(targetStoreId);
            setCompanies((prev) =>
                prev.map((c) => {
                    if (c.id !== company.id) return c;
                    return {
                        ...c,
                        units: c.units.map((unit) => ({
                            ...unit,
                            stores: unit.stores.map((store) => {
                                if (store.id === fromStore.id) {
                                    return { ...store, users: store.users.filter((u) => u.id !== user.id) };
                                }
                                if (store.id === destino) {
                                    return { ...store, users: [...store.users, user] };
                                }
                                return store;
                            }),
                        })),
                    };
                })
            );
            const nome = migrateOptions(company, fromStore.id).find((s) => s.id === destino)?.name || '';
            setMigrateModal((prev) => ({ ...prev, loading: false, done: nome }));
        } catch (err) {
            setMigrateModal((prev) => ({ ...prev, loading: false, error: err.message || 'Erro ao migrar de loja' }));
        }
    };

    // --- Placeholders para cadastro ---
    const handleRegisterCompany = () => {
        saveStateAndNavigate('/companies/create');
    };
    const handleRegisterStore = (e, company) => {
        e.stopPropagation();
        saveStateAndNavigate('/stores/create', { company });
    };
    const handleEditStore = (e, store, company) => {
        e.stopPropagation();
        saveStateAndNavigate('/stores/edit', {
            company,
            store: {
                store_id: store.store_id ?? store.id,
                store_name: store.store_name,
                store_display_name: store.store_display_name,
                app_version_name: store.app_version_name,
                unlock_date: store.unlock_date ?? null,
            },
        });
    };
    const handleEditCompany = (e, company) => {
        e.stopPropagation();
        saveStateAndNavigate('/companies/edit', { company });
    };
    const handleRegisterUnit = (e, company) => {
        e.stopPropagation();
        saveStateAndNavigate('/units/create', { company });
    };
    const handleEditUnit = (e, unit, company) => {
        e.stopPropagation();
        saveStateAndNavigate('/units/edit', { unit, company });
    };
    const handleRegisterUser = (e, company, store) => {
        e.stopPropagation();
        saveStateAndNavigate('/users/create', { company, store, fixedRole: 'salesman' });
    };
    const handleRegisterAdmin = (e, company) => {
        e.stopPropagation();
        saveStateAndNavigate('/users/create-admin', { company });
    };

    // --- Contadores ---
    const totalCompanies = filteredData.companies.length;
    const totalUnits = filteredData.companies.reduce((acc, c) => acc + c.units.length, 0);
    const totalStores = filteredData.companies.reduce(
        (acc, c) => acc + c.units.reduce((a, u) => a + u.stores.length, 0),
        0
    );
    const totalUsers = filteredData.companies.reduce(
        (acc, c) => acc + c.units.reduce((a, u) => a + u.stores.reduce((b, s) => b + s.users.length, 0), 0),
        0
    );

    const translateRole = (role) => {
        const translations = {
            admin: 'Administrador',
            salesman: 'Vendedor',
            manager: 'Gerente',
            director: 'Diretor',
            supervisor: 'Supervisor',
            gerente: 'Gerente',
            vendedor: 'Vendedor',
        };
        return translations[role] || role;
    };

    const getRoleBadgeClass = (role) => {
        switch (role) {
            case 'admin':
            case 'director':
                return 'role-badge role-admin';
            case 'gerente':
            case 'manager':
            case 'supervisor':
                return 'role-badge role-gerente';
            default:
                return 'role-badge role-vendedor';
        }
    };

    if (loading) {
        return <Loading message="Carregando dados..." />;
    }

    return (
        <div className="user-register">
            {/* Header com busca e ações */}
            <div className="user-register-header">
                <h1>Gerenciamento de Usuários</h1>
                <p>Gerencie empresas, lojas e usuários do sistema</p>
            </div>

            {error && (
                <div className="alert alert-error">
                    {error}
                    <button className="btn-action btn-edit" onClick={loadData} style={{ marginLeft: 12 }}>
                        Tentar novamente
                    </button>
                </div>
            )}

            <div className="user-register-toolbar">
                <div className="search-container">
                    <span className="search-icon">&#128269;</span>
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Buscar empresa, loja ou usuário..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="search-clear" onClick={() => setSearchTerm('')}>
                            &times;
                        </button>
                    )}
                </div>
                <div className="toolbar-actions">
                    <button className="btn btn-register btn-company" onClick={handleRegisterCompany}>
                        + Empresa
                    </button>
                </div>
            </div>

            {/* Contadores */}
            <div className="stats-bar">
                <span className="stat-item">
                    <strong>{totalCompanies}</strong> {totalCompanies === 1 ? 'empresa' : 'empresas'}
                </span>
                <span className="stat-separator">|</span>
                <span className="stat-item">
                    <strong>{totalUnits}</strong> {totalUnits === 1 ? 'unidade' : 'unidades'}
                </span>
                <span className="stat-separator">|</span>
                <span className="stat-item">
                    <strong>{totalStores}</strong> {totalStores === 1 ? 'loja' : 'lojas'}
                </span>
                <span className="stat-separator">|</span>
                <span className="stat-item">
                    <strong>{totalUsers}</strong> {totalUsers === 1 ? 'usuário' : 'usuários'}
                </span>
                {unlinkedUsers.length > 0 && (
                    <>
                        <span className="stat-separator">|</span>
                        <span className="stat-item stat-warning">
                            <strong>{filteredData.unlinkedUsers.length}</strong> sem empresa
                        </span>
                    </>
                )}
            </div>

            {/* Filtro por empresa */}
            {companies.length > 1 && (
                <div className="company-filter-bar">
                    {companies.map((company) => (
                        <button
                            key={company.id}
                            className={`company-filter-chip ${selectedCompanyId === company.id ? 'active' : ''}`}
                            onClick={() =>
                                setSelectedCompanyId((prev) =>
                                    prev === company.id ? null : company.id
                                )
                            }
                        >
                            {company.name}
                        </button>
                    ))}
                </div>
            )}

            {/* Seção 1: Hierarquia Empresa > Loja > Usuário */}
            <section className="section-hierarchy">
                {filteredData.companies.length === 0 && searchTerm ? (
                    <div className="empty-state">
                        Nenhum resultado encontrado para &ldquo;{searchTerm}&rdquo;
                    </div>
                ) : filteredData.companies.length === 0 && !searchTerm ? (
                    <div className="empty-state">Nenhuma empresa encontrada</div>
                ) : (
                    filteredData.companies.map((company) => (
                        <div key={company.id} className="company-card">
                            <div
                                className="company-header"
                                onClick={() => toggleCompany(company.id)}
                            >
                                <div className="company-title">
                                    <span
                                        className={`chevron ${expandedCompanies[company.id] ? 'expanded' : ''}`}
                                    >
                                        &#9654;
                                    </span>
                                    <h2>{company.name}</h2>
                                    <span className="count-badge">
                                        {company.units.length}{' '}
                                        {company.units.length === 1 ? 'unidade' : 'unidades'}
                                    </span>
                                </div>
                                <div className="store-header-actions">
                                    <button
                                        className="btn-header btn-header-edit"
                                        onClick={(e) => handleEditCompany(e, company)}
                                        title={`Editar empresa ${company.name}`}
                                    >
                                        ✎ Editar Empresa
                                    </button>
                                    <button
                                        className="btn-header btn-header-admin"
                                        onClick={(e) => handleRegisterAdmin(e, company)}
                                        title={`Adicionar administrador em ${company.name}`}
                                    >
                                        + Administrador
                                    </button>
                                    <button
                                        className="btn-header btn-header-unit"
                                        onClick={(e) => handleRegisterUnit(e, company)}
                                        title={`Adicionar unidade em ${company.name}`}
                                    >
                                        + Unidade
                                    </button>
                                    <button
                                        className="btn-header btn-header-store"
                                        onClick={(e) => handleRegisterStore(e, company)}
                                        title={`Adicionar loja em ${company.name}`}
                                    >
                                        + Loja
                                    </button>
                                </div>
                            </div>

                            {expandedCompanies[company.id] && (
                                <div className="company-body">
                                    {/* Seção Administradores */}
                                    {(() => {
                                        const adminList = getCompanyAdmins(company);
                                        if (adminList.length === 0) return null;
                                        return (
                                            <div className="admin-section">
                                                <div
                                                    className="admin-section-header"
                                                    onClick={() => toggleAdminSection(company.id)}
                                                >
                                                    <div className="admin-section-title">
                                                        <span
                                                            className={`chevron ${expandedAdminSections[company.id] ? 'expanded' : ''}`}
                                                        >
                                                            &#9654;
                                                        </span>
                                                        <h3>Usuários Administradores</h3>
                                                        <span className="count-badge count-badge-sm">
                                                            {adminList.length}
                                                        </span>
                                                    </div>
                                                </div>
                                                {expandedAdminSections[company.id] && (
                                                    <div className="admin-section-body">
                                                        <table className="users-table">
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ width: '40%' }}>Nome</th>
                                                                    <th style={{ width: '10%' }}>Criação</th>
                                                                    <th style={{ width: '12%' }}>Perfil</th>
                                                                    <th style={{ width: '23%' }}>Lojas com Acesso</th>
                                                                    <th className="actions-col" style={{ width: '15%' }}>Ações</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {adminList.map((user) => (
                                                                    <tr key={user.id}>
                                                                        <td className="user-name-cell">
                                                                            <span className="user-fullname">{user.fullname}</span>
                                                                            <CadeadoLogin user={user} />
                                                                            {user.username && (
                                                                                <span className="user-username">{user.username}</span>
                                                                            )}
                                                                        </td>
                                                                        <td>
                                                                            {user.created_at
                                                                                ? new Date(user.created_at).toLocaleDateString('pt-BR')
                                                                                : '-'}
                                                                        </td>
                                                                        <td>
                                                                            <span className={getRoleBadgeClass(user.role)}>
                                                                                {translateRole(user.role)}
                                                                            </span>
                                                                        </td>
                                                                        <td>{user.storeNames.join(', ')}</td>
                                                                        <td className="actions-col">
                                                                            <div className="user-actions">
                                                                                <button
                                                                                    className="btn-action btn-edit"
                                                                                    onClick={() => saveStateAndNavigate('/users/edit-admin', { user, company })}
                                                                                    title="Editar"
                                                                                >
                                                                                    Editar
                                                                                </button>
                                                                                <button
                                                                                    className="btn-action btn-remove"
                                                                                    onClick={() => handleRemove(user, null, company, 'company')}
                                                                                    title={`Remover o acesso à ${company.name} (o login continua)`}
                                                                                >
                                                                                    Remover
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    <div className="admin-units-separator">
                                        <span className="admin-units-separator-title">Unidades</span>
                                    </div>

                                    {company.units.map((unit) => (
                                        <div key={unit.id} className="unit-card">
                                            <div
                                                className="unit-header"
                                                onClick={() => toggleUnit(unit.id)}
                                            >
                                                <div className="unit-title">
                                                    <span
                                                        className={`chevron ${expandedUnits[unit.id] ? 'expanded' : ''}`}
                                                    >
                                                        &#9654;
                                                    </span>
                                                    <h3>{unit.name}</h3>
                                                    {unit.nickname && (
                                                        <span className="unit-nickname">{unit.nickname}</span>
                                                    )}
                                                    <span className="count-badge count-badge-sm">
                                                        {unit.stores.length}{' '}
                                                        {unit.stores.length === 1 ? 'loja' : 'lojas'}
                                                    </span>
                                                </div>
                                                <button
                                                    className="btn-action btn-edit"
                                                    onClick={(e) => handleEditUnit(e, unit, company)}
                                                    title={`Editar unidade ${unit.name}`}
                                                >
                                                    Editar
                                                </button>
                                            </div>

                                            {expandedUnits[unit.id] && (
                                                <div className="unit-body">
                                                    {unit.stores.map((store) => {
                                                      const vendedores = store.users.filter(isSalesman);
                                                      return (
                                                        <div key={store.id} className="store-card">
                                                            <div
                                                                className="store-header"
                                                                onClick={() => toggleStore(store.id)}
                                                            >
                                                                <div className="store-title">
                                                                    <span
                                                                        className={`chevron ${expandedStores[store.id] ? 'expanded' : ''}`}
                                                                    >
                                                                        &#9654;
                                                                    </span>
                                                                    <h4>{store.name}</h4>
                                                                    <span className="count-badge count-badge-sm">
                                                                        {vendedores.length}{' '}
                                                                        {vendedores.length === 1
                                                                            ? 'vendedor'
                                                                            : 'vendedores'}
                                                                    </span>
                                                                    <span className="app-version-badge" title="Versão do app da loja">
                                                                        app: {store.app_version_name || 'default'}
                                                                    </span>
                                                                </div>
                                                                <div className="store-header-buttons">
                                                                    <button
                                                                        className="btn-header btn-header-store"
                                                                        onClick={(e) => handleEditStore(e, store, company)}
                                                                        title={`Editar loja ${store.name}`}
                                                                    >
                                                                        ✎ Editar loja
                                                                    </button>
                                                                    <button
                                                                        className="btn-header btn-header-user"
                                                                        onClick={(e) => handleRegisterUser(e, company, store)}
                                                                        title={`Adicionar vendedor em ${store.name}`}
                                                                    >
                                                                        + Vendedor
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            {expandedStores[store.id] && (
                                                                <div className="store-body">
                                                                    {vendedores.length === 0 ? (
                                                                        <div className="empty-store">
                                                                            Nenhum vendedor nesta loja
                                                                        </div>
                                                                    ) : (
                                                                        <table className="users-table">
                                                                            <thead>
                                                                                <tr>
                                                                                    <th style={{ width: '29%' }}>Nome</th>
                                                                                    <th style={{ width: '8%' }}>Criação</th>
                                                                                    <th style={{ width: '8%' }}>Início</th>
                                                                                    <th style={{ width: '8%' }}>Perfil</th>
                                                                                    <th style={{ width: '7%', textAlign: 'center' }}>Visível</th>
                                                                                    <th style={{ width: '7%', textAlign: 'center' }}>Ativo</th>
                                                                                    <th style={{ width: '7%', textAlign: 'center' }}>Feedbacks</th>
                                                                                    <th style={{ width: '9%', textAlign: 'center' }}>App</th>
                                                                                    <th style={{ width: '11%', textAlign: 'center' }}>Onboarding</th>
                                                                                    <th className="actions-col" style={{ width: '250px', textAlign: 'right' }}>
                                                                                        Ações
                                                                                    </th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {vendedores.map((user) => (
                                                                                    <tr
                                                                                        key={user.id}
                                                                                        className={
                                                                                            user.hidden
                                                                                                ? 'user-hidden'
                                                                                                : ''
                                                                                        }
                                                                                    >
                                                                                        <td className="user-name-cell">
                                                                                            <span className="user-fullname">
                                                                                                {user.fullname}
                                                                                            </span>
                                                                                            <CadeadoLogin user={user} />
                                                                                            {user.username && (
                                                                                                <span className="user-username">
                                                                                                    {user.username}
                                                                                                </span>
                                                                                            )}
                                                                                        </td>
                                                                                        <td>
                                                                                            {user.created_at
                                                                                                ? new Date(user.created_at).toLocaleDateString('pt-BR')
                                                                                                : '-'}
                                                                                        </td>
                                                                                        <td>
                                                                                            {user.recordings_start
                                                                                                ? new Date(user.recordings_start).toLocaleDateString('pt-BR')
                                                                                                : '-'}
                                                                                        </td>
                                                                                        <td>
                                                                                            <span
                                                                                                className={getRoleBadgeClass(
                                                                                                    user.role
                                                                                                )}
                                                                                            >
                                                                                                {translateRole(user.role)}
                                                                                            </span>
                                                                                        </td>
                                                                                        <td style={{ textAlign: 'center', fontSize: '18px' }}>
                                                                                            {user.is_visible ? '✅' : '❌'}
                                                                                        </td>
                                                                                        <td style={{ textAlign: 'center', fontSize: '18px' }}>
                                                                                            {user.is_active ? '✅' : '❌'}
                                                                                        </td>
                                                                                        <td style={{ textAlign: 'center', fontSize: '18px' }}>
                                                                                            {user.feedback_read_enabled ? '✅' : '❌'}
                                                                                        </td>
                                                                                        <td style={{ textAlign: 'center' }}>
                                                                                            {user.role === 'salesman' ? (
                                                                                                <span className="app-version-badge">
                                                                                                    {user.app_version_name || store.app_version_name || 'default'}
                                                                                                    {!user.app_version_name && <span className="app-version-inherit"> (loja)</span>}
                                                                                                </span>
                                                                                            ) : '-'}
                                                                                        </td>
                                                                                        <td style={{ textAlign: 'center', fontSize: '12px' }}>
                                                                                            {user.role === 'salesman'
                                                                                                ? (user.onboarding_completed_at
                                                                                                    ? new Date(user.onboarding_completed_at).toLocaleString('pt-BR')
                                                                                                    : '—')
                                                                                                : '-'}
                                                                                        </td>
                                                                                        <td className="actions-col">
                                                                                            <div className="user-actions">
                                                                                                <button
                                                                                                    className="btn-action btn-edit"
                                                                                                    onClick={() =>
                                                                                                        handleEdit(
                                                                                                            user,
                                                                                                            company
                                                                                                        )
                                                                                                    }
                                                                                                    title="Editar"
                                                                                                >
                                                                                                    Editar
                                                                                                </button>
                                                                                                <button
                                                                                                    className="btn-action btn-migrate"
                                                                                                    onClick={() =>
                                                                                                        handleMigrate(
                                                                                                            user,
                                                                                                            store,
                                                                                                            company
                                                                                                        )
                                                                                                    }
                                                                                                    title="Migrar para outra loja"
                                                                                                >
                                                                                                    Migrar loja
                                                                                                </button>
                                                                                                <button
                                                                                                    className="btn-action btn-remove"
                                                                                                    onClick={() =>
                                                                                                        handleRemove(
                                                                                                            user,
                                                                                                            store.id,
                                                                                                            company
                                                                                                        )
                                                                                                    }
                                                                                                    title="Remover"
                                                                                                >
                                                                                                    Remover
                                                                                                </button>
                                                                                            </div>
                                                                                        </td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                      );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </section>

            {/* Seção 2: Usuários sem empresa */}
            {filteredData.unlinkedUsers.length > 0 && (
                <section className="section-unlinked">
                    <div className="section-unlinked-header">
                        <h2>Usuários sem Empresa</h2>
                        <span className="count-badge count-badge-warning">
                            {filteredData.unlinkedUsers.length}
                        </span>
                    </div>
                    <div className="unlinked-list">
                        {filteredData.unlinkedUsers.map((user) => (
                            <div key={user.id} className="unlinked-user-card">
                                <div className="unlinked-user-info">
                                    <span className="unlinked-user-name">{user.fullname}</span>
                                    {user.username && (
                                        <span className="unlinked-user-username">
                                            @{user.username}
                                        </span>
                                    )}
                                    <span className={getRoleBadgeClass(user.role)}>
                                        {translateRole(user.role)}
                                    </span>
                                </div>
                                <div className="unlinked-user-actions">
                                    <button
                                        className="btn-action btn-edit"
                                        onClick={() => handleEdit(user)}
                                    >
                                        Editar
                                    </button>
                                    <button
                                        className="btn-action btn-remove"
                                        onClick={() => {
                                            avisar(`Remover "${user.fullname}" (funcionalidade será implementada)`, { titulo: 'Em breve' });
                                        }}
                                    >
                                        Remover
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {migrateModal.open && (
                <div className="modal-overlay" onClick={cancelMigrate}>
                    <div className="modal-confirm modal-migrate" onClick={(e) => e.stopPropagation()}>
                        {migrateModal.done ? (
                            <>
                                <div className="modal-confirm-icon">✅</div>
                                <h3 className="modal-confirm-title">Vendedor migrado</h3>
                                <p className="modal-confirm-message">
                                    <strong>{migrateModal.user?.fullname}</strong> agora está em{' '}
                                    <strong>{migrateModal.done}</strong>.
                                </p>
                                <div className="migrate-warning">
                                    <strong>Peça para o vendedor sair e entrar de novo no app.</strong> A loja
                                    fica gravada no aparelho no login e só é reescrita em um login novo — até
                                    lá as gravações continuam entrando na loja antiga.
                                </div>
                                <div className="modal-confirm-actions">
                                    <button className="modal-btn modal-btn-cancel" onClick={cancelMigrate}>
                                        Fechar
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="modal-confirm-icon">🔀</div>
                                <h3 className="modal-confirm-title">Migrar de loja</h3>
                                <p className="modal-confirm-message">
                                    <strong>{migrateModal.user?.fullname}</strong> sai de{' '}
                                    <strong>{migrateModal.fromStore?.name}</strong> e passa para:
                                </p>
                                <select
                                    className="migrate-select"
                                    value={migrateModal.targetStoreId}
                                    onChange={(e) =>
                                        setMigrateModal((prev) => ({ ...prev, targetStoreId: e.target.value, error: '' }))
                                    }
                                    disabled={migrateModal.loading}
                                >
                                    <option value="">Selecione a loja de destino…</option>
                                    {migrateOptions(migrateModal.company, migrateModal.fromStore?.id).map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.unitName ? `${s.unitName} — ${s.name}` : s.name}
                                        </option>
                                    ))}
                                </select>
                                <ul className="migrate-facts">
                                    <li>O cadastro é o mesmo: login, senha e histórico do vendedor não mudam.</li>
                                    <li>Os atendimentos já gravados ficam na loja onde aconteceram — nenhum número muda nas duas lojas.</li>
                                    <li>Daqui pra frente valem os prompts, metas, régua de pontos e whitelabel da loja nova.</li>
                                    <li>O vendedor precisa sair e entrar de novo no app para gravar na loja nova.</li>
                                </ul>
                                {migrateModal.error && (
                                    <div className="migrate-error">{migrateModal.error}</div>
                                )}
                                <div className="modal-confirm-actions">
                                    <button
                                        className="modal-btn modal-btn-cancel"
                                        onClick={cancelMigrate}
                                        disabled={migrateModal.loading}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        className="modal-btn modal-btn-migrate"
                                        onClick={confirmMigrate}
                                        disabled={migrateModal.loading || !migrateModal.targetStoreId}
                                    >
                                        {migrateModal.loading ? 'Migrando...' : 'Migrar'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {removeModal.open && (
                <div className="modal-overlay" onClick={cancelRemove}>
                    <div className="modal-confirm" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-confirm-icon">⚠️</div>
                        <h3 className="modal-confirm-title">Confirmar Remoção</h3>
                        {removeModal.scope === 'company' ? (
                            <>
                                <p className="modal-confirm-message">
                                    Remover o acesso de <strong>{removeModal.user?.fullname}</strong> à{' '}
                                    <strong>{removeModal.company?.name}</strong>?
                                </p>
                                <p className="modal-confirm-sub">
                                    A pessoa deixa de ver as lojas desta empresa. O login e o acesso a outras empresas continuam.
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="modal-confirm-message">
                                    Tem certeza que deseja remover <strong>{removeModal.user?.fullname}</strong>?
                                </p>
                                <p className="modal-confirm-sub">Esta ação não pode ser desfeita.</p>
                            </>
                        )}
                        {removeModal.error && (
                            <div className="migrate-error">{removeModal.error}</div>
                        )}
                        <div className="modal-confirm-actions">
                            <button
                                className="modal-btn modal-btn-cancel"
                                onClick={cancelRemove}
                                disabled={removeModal.loading}
                            >
                                Cancelar
                            </button>
                            <button
                                className="modal-btn modal-btn-confirm"
                                onClick={confirmRemove}
                                disabled={removeModal.loading}
                            >
                                {removeModal.loading ? 'Removendo...' : 'Remover'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserRegister;
